import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http, money } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const order_id = params.get("order_id");
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("checking"); // checking | success | failed | manual
  const { clear } = useCart();

  // Load order
  useEffect(() => {
    if (order_id) {
      http.get(`/checkout/order/${order_id}`).then((r) => setOrder(r.data)).catch(() => { /* noop */ });
    }
  }, [order_id]);

  // Poll stripe status (max ~30s)
  useEffect(() => {
    if (!session_id) {
      if (order_id) {
        setStatus("success");
        clear();
      }
      return;
    }
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await http.get(`/payments/stripe/status/${session_id}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          clear();
          if (order_id) {
            const o = await http.get(`/checkout/order/${order_id}`);
            setOrder(o.data);
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
      } catch (_e) { /* keep polling */ }
      if (attempts < 15) setTimeout(tick, 2000);
      else setStatus("manual");
    };
    tick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_id, order_id]);

  return (
    <div className="bls-container py-20 max-w-2xl">
      <div className="bg-white rounded-3xl border border-black/5 p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[#7f77dd] animate-spin" />
            <h1 className="mt-6 font-display text-3xl font-bold">Confirming your payment…</h1>
            <p className="mt-2 text-black/55">Hang tight — this usually takes a few seconds.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-bold">Order confirmed!</h1>
            <p className="mt-2 text-black/55">Thanks for your order. We&apos;ll email you a confirmation shortly.</p>
            {order && (
              <div className="mt-8 text-left bg-[#fafaf8] rounded-2xl p-6 space-y-2 text-sm">
                <p><span className="text-black/55">Order number:</span> <span className="font-mono font-semibold">{order.order_number}</span></p>
                <p><span className="text-black/55">Total:</span> <span className="font-semibold">{money(order.total)}</span></p>
                <p><span className="text-black/55">Payment:</span> <span className="capitalize">{order.payment_method} — {order.payment_status}</span></p>
                <p><span className="text-black/55">Ship to:</span> {order.address?.full_name}, {order.address?.city}</p>
              </div>
            )}
            <Link to="/products" className="bls-btn-primary mt-8 inline-flex">Continue shopping</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-6 font-display text-3xl font-bold">Payment failed or expired</h1>
            <Link to="/cart" className="bls-btn-primary mt-6 inline-flex">Try again</Link>
          </>
        )}
        {status === "manual" && (
          <>
            <h1 className="font-display text-2xl font-bold">Still confirming…</h1>
            <p className="text-black/55 mt-2">Reload this page in a moment, or check your email.</p>
          </>
        )}
      </div>
    </div>
  );
}
